import { Button, Flex, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import { Schemas } from '@api-client/generated/types';
import { AccountingUsageStatusTag } from '@entities/accounting-plan';
import {
  PLAN_TYPE_LABEL,
  STATUS_COLOR,
  STATUS_RANK,
  formatDate,
  formatPlanAmount,
} from '@entities/accounting-plan/format';

import * as S from './styled';

export type UsageRow = Schemas.AdminAccountingUsageRowDto;

const { Text } = Typography;

const byNumber =
  (pick: (row: UsageRow) => number | null) => (a: UsageRow, b: UsageRow) =>
    (pick(a) ?? -1) - (pick(b) ?? -1);

/** Numbers, status and dates sort descending on the first click (spec §3.3); text ascending. */
const DESC_FIRST = ['descend', 'ascend'] as const;

export const getColumns = (
  onEdit: (row: UsageRow) => void,
  editable: boolean
): ColumnsType<UsageRow> => [
  {
    title: 'Company',
    dataIndex: 'companyName',
    width: 220,
    defaultSortOrder: 'ascend',
    sorter: (a, b) => a.companyName.localeCompare(b.companyName),
    render: (_, row) => (
      <Link to={`/companies/${row.companyId}/info`}>{row.companyName}</Link>
    ),
  },
  {
    title: 'Plan type',
    dataIndex: 'planType',
    width: 130,
    sorter: (a, b) => (a.planType ?? '').localeCompare(b.planType ?? ''),
    render: (_, row) =>
      row.planType ? (
        PLAN_TYPE_LABEL[row.planType]
      ) : (
        <Text type="secondary">No plan</Text>
      ),
  },
  {
    title: 'Plan limit',
    dataIndex: 'planLimit',
    width: 100,
    sorter: byNumber((row) => row.planLimit),
    sortDirections: [...DESC_FIRST],
    render: (_, row) =>
      row.planLimit === null
        ? '—'
        : formatPlanAmount(row.planLimit, row.planType),
  },
  {
    title: 'Bookable docs',
    dataIndex: 'bookableDocuments',
    width: 110,
    sorter: byNumber((row) => row.bookableDocuments),
    sortDirections: [...DESC_FIRST],
    render: (_, row) => (
      <S.Metric active={row.planType === 'documents'}>
        {formatPlanAmount(row.bookableDocuments, 'documents')}
      </S.Metric>
    ),
  },
  {
    title: 'Turnover',
    dataIndex: 'turnover',
    width: 120,
    sorter: byNumber((row) => row.turnover),
    sortDirections: [...DESC_FIRST],
    render: (_, row) => (
      <S.Metric active={row.planType === 'legacy_turnover'}>
        {formatPlanAmount(row.turnover, 'legacy_turnover')}
      </S.Metric>
    ),
  },
  {
    title: 'Usage of plan',
    key: 'usage',
    width: 160,
    sorter: byNumber((row) => (row.planType ? row.percent : null)),
    sortDirections: [...DESC_FIRST],
    render: (_, row) =>
      row.planType && row.planLimit !== null ? (
        <Flex gap={2} vertical>
          <Flex justify="space-between" gap={6}>
            <S.Small>
              {formatPlanAmount(row.used, row.planType)} /{' '}
              {formatPlanAmount(row.planLimit, row.planType)}
            </S.Small>
            <S.Small
              style={{ color: STATUS_COLOR[row.status], fontWeight: 600 }}
            >
              {Math.round(row.percent)}%
            </S.Small>
          </Flex>
          <S.Track>
            <S.Fill
              style={{
                width: `${Math.min(row.percent, 100)}%`,
                background: STATUS_COLOR[row.status],
              }}
            />
          </S.Track>
        </Flex>
      ) : (
        '—'
      ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    width: 120,
    sorter: byNumber((row) => STATUS_RANK[row.status]),
    sortDirections: [...DESC_FIRST],
    render: (_, row) => <AccountingUsageStatusTag status={row.status} />,
  },
  {
    title: 'Updated',
    dataIndex: 'updatedAt',
    width: 90,
    sorter: byNumber((row) =>
      row.updatedAt ? Date.parse(row.updatedAt) : null
    ),
    sortDirections: [...DESC_FIRST],
    render: (_, row) => (
      <S.Small type="secondary">{formatDate(row.updatedAt)}</S.Small>
    ),
  },
  {
    key: 'actions',
    width: 100,
    align: 'right',
    render: (_, row) => (
      <Tooltip
        title={editable ? undefined : 'Plans are edited in the current year'}
      >
        <Button size="small" disabled={!editable} onClick={() => onEdit(row)}>
          Edit plan
        </Button>
      </Tooltip>
    ),
  },
];
